// Tasks regular helpers split from the legacy mega-file.
import { sql } from "../../base";
import { regularTaskHistory } from "../generated/regularTaskHistory";
import type { Task } from "../../types";

export const regularTasks = {
  async completeRegularTask(id: string, userId: string, completionNotes?: string): Promise<Task> {
      // Handle regular task completion with next instance generation
      const currentTask = await this.findById(id, userId);
      if (!currentTask || !currentTask.is_regular) {
        throw new Error('Task not found or is not a regular task');
      }
      
      // First, mark the current task as complete
      const updatedDescription = completionNotes 
        ? currentTask.description 
          ? `${currentTask.description}\n\nCompletion Notes: ${completionNotes}`
          : `Completion Notes: ${completionNotes}`
        : currentTask.description;
        
      const completedTask = await this.update(id, userId, {
        status: 'Complete',
        end_date: new Date(),
        description: updatedDescription
      });
      
      // Record the completion in history
      await regularTaskHistory.recordCompletion(id, userId);
      
      // Calculate the next due date
      const frequency = currentTask.frequency || 'Daily';
      let currentDueDate: Date;
      
      if (currentTask.next_due_date) {
        const dateValue = currentTask.next_due_date;
        if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          currentDueDate = new Date(dateValue + 'T00:00:00.000Z');
        } else {
          const dateStr = new Date(dateValue).toISOString().split('T')[0];
          currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
        }
      } else if (currentTask.start_date) {
        const dateStr = new Date(currentTask.start_date).toISOString().split('T')[0];
        currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
      } else {
        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];
        currentDueDate = new Date(dateStr + 'T00:00:00.000Z');
      }
      
      // Import calculateNextDueDate if needed
      const { calculateNextDueDate } = await import('../../../dateUtils');
      const nextDueDate = calculateNextDueDate(currentDueDate, frequency, currentTask.recurrence_day);
      const nextDueDateString = nextDueDate.toISOString().split('T')[0];
      
      // Check if a task already exists for the next due date
      const existingNextTask = await sql`
        SELECT id FROM tasks
        WHERE user_id = ${userId}
        AND LOWER(TRIM(title)) = LOWER(TRIM(${currentTask.title}))
        AND LOWER(TRIM(COALESCE(description, ''))) = LOWER(TRIM(COALESCE(${currentTask.description || ''}, '')))
        AND frequency = ${frequency}
        AND is_regular = true
        AND status != 'Complete'
        AND (
          next_due_date = ${nextDueDateString}::date
          OR DATE(start_date AT TIME ZONE 'UTC') = ${nextDueDateString}
        )
        LIMIT 1
      `;
      
      if (existingNextTask.length === 0) {
        // Create the new instance only if it doesn't already exist
        // Check if the next task is for today or in the past
        const now = new Date();
        const todayMidnight = new Date(Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          0, 0, 0, 0
        ));
        
        const newTaskData = {
          title: currentTask.title,
          description: currentTask.description,
          priority: currentTask.priority,
          is_regular: true,
          frequency: currentTask.frequency,
          recurrence_day: currentTask.recurrence_day,
          start_date: nextDueDate,
          deadline: nextDueDate,
          next_due_date: nextDueDateString,
          status: 'To Do' as const,
          is_live: nextDueDate <= todayMidnight,  // Compare dates at midnight UTC
          user_id: userId,
          goal_id: currentTask.goal_id
        };
        
        const newTask = await this.create(newTaskData);
        
        // Record the generation of the new task
        await regularTaskHistory.recordGeneration(currentTask.id, newTask.id, userId, nextDueDate);
      }
      
      return completedTask;
    },
};
